import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const ProductPhotos = ({
  images,
  setImages,
  oldImages,
  setImagesToRemove,
  removedImages,
  setRemovedImages,
  imagesToRemove,
  isEditMode = false,
  imageFocus = {},
  setImageFocus,
}) => {
  const { t } = useTranslation("addProductsHeader");
  const fileInputRef = useRef(null);
  const [focusOverlay, setFocusOverlay] = useState({
    show: false,
    image: null,
    type: "new",
  });
  const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const overlayRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageError, setImageError] = useState("");
  // Target dimensions for overlay display
  const TARGET_WIDTH = 1700;
  const TARGET_HEIGHT = 450;
  const TARGET_ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;

  useEffect(() => {
    sessionStorage.setItem("uploadedImages", JSON.stringify(images));
  }, [images]);

  // Auto-clear image error after 10 seconds
  useEffect(() => {
    if (imageError) {
      const timer = setTimeout(() => {
        setImageError("");
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [imageError]);

  

  const handleImageUpload = async (event) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;
    
    // Check if adding these files would exceed the limit of 5 images
    if (images.length + selectedFiles.length > 5) {
      setImageError(t("productPhotos.maxImagesError"));
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    // Check file sizes - 5 MB = 5 * 1024 * 1024 bytes
    const maxFileSize = 5 * 1024 * 1024; // 5 MB in bytes
    const oversizedFiles = Array.from(selectedFiles).filter(file => file.size > maxFileSize);
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(file => file.name).join(', ');
      setImageError(t("productPhotos.fileSizeError", { files: fileNames }));
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    // Clear any previous errors
    setImageError("");
    setIsProcessing(true);

    try {
      const processedImages = await Promise.all(
        Array.from(selectedFiles).map((file) => file)
      );

      setImages([...images, ...processedImages]);
    } catch (error) {
      console.error("Error processing images:", error);
      setImageError(t("productPhotos.uploadError"));
    } finally {
      setIsProcessing(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const removeImage = (file) => {
    setImages(images.filter((image) => image !== file));
    // Remove focus data for this image
    if (setImageFocus) {
      const newFocus = { ...imageFocus };
      delete newFocus[file.name];
      setImageFocus(newFocus);
    }
  };

  const removeOldImage = (url) => {
    setRemovedImages([...removedImages, url]);
    setImagesToRemove(oldImages.filter((image) => image !== url));
    // Remove focus data for this image
    if (setImageFocus) {
      const newFocus = { ...imageFocus };
      delete newFocus[url];
      setImageFocus(newFocus);
    }
  };

  const openFocusSelector = (image, type = "new") => {
    const imageKey = type === "new" ? image.name : image;
    const currentFocus = imageFocus[imageKey] || { x: 50, y: 50 };
    setFocusPoint(currentFocus);
    setFocusOverlay({ show: true, image, type });
  };

  const closeFocusSelector = () => {
    setFocusOverlay({ show: false, image: null, type: "new" });
    setIsDragging(false);
  };

  const getPositionFromEvent = (event) => {
    if (!overlayRef.current) return null;

    const rect = overlayRef.current.getBoundingClientRect();
    let clientX, clientY;

    if (event.type.includes("touch")) {
      clientX = event.touches[0]?.clientX || event.changedTouches[0]?.clientX;
      clientY = event.touches[0]?.clientY || event.changedTouches[0]?.clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
    setIsDragging(true);
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleMouseUp = (event) => {
    if (isDragging) {
      event.preventDefault();
      setIsDragging(false);
    }
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    setIsDragging(true);
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleTouchMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const position = getPositionFromEvent(event);
    if (position) {
      setFocusPoint(position);
    }
  };

  const handleTouchEnd = (event) => {
    if (isDragging) {
      event.preventDefault();
      setIsDragging(false);
    }
  };

  // Add global event listeners for mouse events when dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = (e) => handleMouseUp(e);
      const handleGlobalTouchMove = (e) => handleTouchMove(e);
      const handleGlobalTouchEnd = (e) => handleTouchEnd(e);

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [isDragging, focusPoint]);

  const saveFocusPoint = () => {
    if (!setImageFocus) return;

    const imageKey =
      focusOverlay.type === "new"
        ? focusOverlay.image.name
        : focusOverlay.image;
    setImageFocus({
      ...imageFocus,
      [imageKey]: focusPoint,
    });
    closeFocusSelector();
  };

  const getImageStyle = (imageKey) => {
    const focus = imageFocus[imageKey];
    if (!focus) return {};

    return {
      objectPosition: `${focus.x}% ${focus.y}%`,
    };
  };

  const getFocusPreview = (imageKey) => {
    const focus = imageFocus[imageKey];
    if (!focus) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute w-2 h-2 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-lg"
          style={{
            left: `${focus.x}%`,
            top: `${focus.y}%`,
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {oldImages &&
          oldImages.length > 0 &&
          oldImages.map((url, index) => (
            <div
              key={index}
              className="border-[1px] border-[#dbdbdb] relative w-[172px] h-[132px] overflow-hidden rounded-[4px] group"
            >
              <img
                className="object-cover w-full h-full transition-transform duration-200"
                src={url}
                alt={t("productPhotos.removeAlt")}
                loading="lazy"
                style={getImageStyle(url)}
              />
              {getFocusPreview(url)}

              {/* Action buttons */}
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={() => openFocusSelector(url, "old")}
                  title="Set focus point"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="12"
                      y1="2"
                      x2="12"
                      y2="6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="12"
                      y1="18"
                      x2="12"
                      y2="22"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="2"
                      y1="12"
                      x2="6"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="18"
                      y1="12"
                      x2="22"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
                <button
                  className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={() => removeOldImage(url)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}

        {images.map((file, index) => (
          <div
            key={index}
            className="border-[1px] border-[#dbdbdb] relative w-[172px] h-[132px] overflow-hidden rounded-[4px] group"
          >
            <img
              className="object-cover w-full h-full transition-transform duration-200"
              src={URL.createObjectURL(file)}
              alt={file.name}
              loading="lazy"
              style={getImageStyle(file.name)}
            />
            {getFocusPreview(file.name)}

            {/* Action buttons */}
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={() => openFocusSelector(file, "new")}
                title="Set focus point"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="12"
                    y1="18"
                    x2="12"
                    y2="22"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="2"
                    y1="12"
                    x2="6"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="18"
                    y1="12"
                    x2="22"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={() => removeImage(file)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
    
        {(isEditMode
          ? images.length + imagesToRemove.length < 5
          : images.length < 5) && (
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="upload-image"
            ref={fileInputRef}
          />
        )}

        {(isEditMode
          ? images.length + imagesToRemove.length < 5
          : images.length < 5) && (
          <div className="relative">
            <label
              htmlFor="upload-image"
              className={`bg-white border-[1px] border-[#dbdbdb] cursor-pointer rounded-[4px] flex justify-center items-center w-[172px] h-[132px] hover:border-blue-400 transition-colors duration-200 ${
                isProcessing ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <div className="flex flex-col items-center gap-2 text-gray-500">
                {isProcessing ? (
                  <>
                    <svg
                      className="animate-spin h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-sm">{t("focus.uploading")}...</span>
                  </>
                ) : (
                  <>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="text-sm">{t("focus.uploadImage")}</span>
                  </>
                )}
              </div>
            </label>
            <p className="absolute top-[-16px] right-[-16px] text-xl text-[#FF0000]">
              {t("productPhotos.requiredAsterisk")}
            </p>
          </div>
        )}
      </div>
      
     
      {imageError && (
          <div className="w-full text-red-500 text-sm">
            {imageError}
          </div>
        )}
      {/* Focus Point Selection Overlay */}
      {focusOverlay.show && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pt-[100px]">
          <div className="bg-white rounded-2xl p-6 max-w-7xl max-h-[90vh] w-full overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("focus.setFocus")}
              </h3>
              <button
                onClick={closeFocusSelector}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
                  {t("focus.dragInstruction")}
            </p>

            {/* Image Preview Container */}
            <div className="mb-6">
              <div
                ref={overlayRef}
                className={`relative mx-auto border-2 border-gray-300 rounded-2xl overflow-hidden shadow-lg select-none ${
                  isDragging ? "cursor-grabbing" : "cursor-grab"
                }`}
                style={{
                  width: Math.min(TARGET_WIDTH, window.innerWidth - 100),
                  height: Math.min(
                    TARGET_HEIGHT,
                    (window.innerWidth - 100) / TARGET_ASPECT_RATIO
                  ),
                  maxWidth: "100%",
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <img
                  src={
                    focusOverlay.type === "new"
                      ? URL.createObjectURL(focusOverlay.image)
                      : focusOverlay.image
                  }
                  alt="Focus selection"
                  className="w-full h-full object-cover pointer-events-none"
                  style={{
                    objectPosition: `${focusPoint.x}% ${focusPoint.y}%`,
                  }}
                  draggable={false}
                />

                {/* Focus Point Indicator */}
                <div
                  className={`absolute w-6 h-6 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 border-4 border-white shadow-lg pointer-events-none transition-all duration-150 ${
                    isDragging ? "scale-125 animate-pulse" : ""
                  }`}
                  style={{
                    left: `${focusPoint.x}%`,
                    top: `${focusPoint.y}%`,
                  }}
                />

                {/* Crosshair */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${focusPoint.x}%`,
                    top: `${focusPoint.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div
                    className={`h-0.5 bg-white shadow-md transition-all duration-150 ${
                      isDragging ? "w-12" : "w-8"
                    } -translate-x-1/2`}
                  ></div>
                  <div
                    className={`w-0.5 bg-white shadow-md transition-all duration-150 ${
                      isDragging ? "h-12" : "h-8"
                    } -translate-y-1/2 -translate-x-1/2`}
                  ></div>
                </div>

                {/* Drag instruction overlay */}
                {!isDragging && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200">
                    <div className="bg-black bg-opacity-60 text-white px-3 py-1 rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity duration-200">
                      {t("focus.dragClick")}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center mt-2 text-sm text-gray-500">
                {t("focus.point")}: {Math.round(focusPoint.x)}%,{" "}
                {Math.round(focusPoint.y)}%
                {isDragging && (
                  <span className="ml-2 text-blue-500 font-medium">
                    {t("focus.dragging")}...
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={closeFocusSelector}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                {t("focus.cancel")}
              </button>
              <button
                onClick={saveFocusPoint}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
              >
                {t("focus.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductPhotos;
